import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  FilterAccordion,
  TextField,
  TextFieldProps,
  useOnClickOutside,
} from '@avantstay/backoffice-core'

import * as S from '@new/__global/components/Filters/Filters.styles'

import { FiltersSharedProps } from '../Filters.types'

interface InputBasedFilterProps extends FiltersSharedProps, Omit<TextFieldProps, 'onChange'> {
  title: string
  initialValue?: string
  tagSuffix?: string
  mask?: string
  onChange: (value?: string) => void
}

export const InputBasedFilter: FC<InputBasedFilterProps> = ({
  title,
  initialValue,
  tagSuffix,
  isSidebarFilter,
  mask,
  onChange,
  isAccordionOpen,
  ...textFieldProps
}) => {
  const [localValue, setLocalValue] = useState<string | undefined>(initialValue)

  const wrapperRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputOnChange = useCallback(
    debounce((value?: string) => {
      onChange(value)
    }, 300),
    [],
  )

  const handleSubmitValue = () => {
    if (localValue !== initialValue) {
      debouncedInputOnChange(localValue)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitValue)

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue, isSidebarFilter])

  // This clears selection when we run propertiesListStore.resetFilters
  useEffect(() => {
    if (initialValue === undefined) {
      setLocalValue(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue])

  const handleOnClear = () => {
    onChange(undefined)
    setLocalValue('')
  }

  const filterValue = tagSuffix ? `${localValue} ${tagSuffix}` : localValue

  const Component = mask ? TextField.SmallMasked : TextField.Small

  return (
    <FilterAccordion
      title={title}
      onClearFilter={handleOnClear}
      ref={wrapperRef}
      filterValue={filterValue}
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
    >
      <S.FilterInternalWrapper>
        <Component
          value={localValue || ''}
          mask={mask}
          onChange={({ currentTarget }) => setLocalValue(currentTarget.value)}
          {...textFieldProps}
        />
      </S.FilterInternalWrapper>
    </FilterAccordion>
  )
}
